// CRM-iframe deep-link: open a specific peer when the parent SPA posts a
// `tg:openChat` message (the lead drawer's "Открыть чат"). This lives in
// its own module rather than crmBridge.ts on purpose: crmBridge is also
// imported by the gramjs worker (for the WSS patches) where there is no
// `window` and no global store, so it must stay free of `../global`. This
// file is imported only from index.tsx on the main thread.

import { getActions, getGlobal } from '../global';
import { fetchChatByUsername } from '../global/actions/api/chats';
import { selectChat, selectUser } from '../global/selectors';

import { getCrmOrigin } from './crmBridge';

interface CrmOpenChatTarget {
  peerId?: string;
  username?: string;
  // Open the channel's Direct Messages (linked monoforum) instead of the
  // broadcast channel. Routed through openChatByUsername({ isDirect }).
  direct?: boolean;
}

const READY_POLL_INTERVAL_MS = 250;
const READY_TIMEOUT_MS = 20000;

let isListening = false;

// Opening a chat before telegram-tt is connected and synced silently
// no-ops (openChat can't resolve a peer whose user/access_hash isn't cached
// yet). There's no single "ready" event observable from outside an action,
// so poll the global until the session is live or we give up.
function whenClientReady(): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const check = () => {
      const global = getGlobal();
      if (global.connectionState === 'connectionStateReady' && global.isSynced) {
        resolve(true);
        return;
      }
      if (Date.now() - startedAt > READY_TIMEOUT_MS) {
        resolve(false);
        return;
      }
      setTimeout(check, READY_POLL_INTERVAL_MS);
    };
    check();
  });
}

function postOpenChatFailed(origin: string) {
  if (!window.parent || window.parent === window) return;
  try {
    window.parent.postMessage({ type: 'tg:openChatFailed' }, origin);
  } catch {
    // Cross-origin parent went away — best effort only.
  }
}

// Poll the global until the monoforum chat is in state. It lands asynchronously
// after loadFullChat (it rides in the full-chat response's `chats`), so we can't
// open it synchronously. We intentionally resolve as soon as the chat is present
// (verified working end-to-end) rather than waiting for a non-`min` entity: a
// GetFullChannel-sourced chat is already usable, and gating on `!isMin` risks
// timing out and falling back to the broadcast channel if the server returns a
// min monoforum. Bounded.
function whenChatLoaded(chatId: string, timeoutMs = 8000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const check = () => {
      if (selectChat(getGlobal(), chatId)) {
        resolve(true);
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(check, 150);
    };
    check();
  });
}

// Open a channel's Direct Messages (its linked monoforum). The plain isDirect
// deep-link doesn't work from a cold open: fetchChatByUsername only caches the
// channel itself, so selectChat(linkedMonoforumId) misses and it falls back to
// the broadcast channel. We instead mirror telegram-tt's native "Direct
// Messages" header button: resolve the channel, loadFullChat (which pulls the
// monoforum chat — with its access_hash — into state), then openChat the
// monoforum by id.
async function openChannelDirect(username: string, origin: string): Promise<void> {
  const actions = getActions();
  const channel = await fetchChatByUsername(getGlobal(), username);
  const monoId = channel?.linkedMonoforumId;
  if (channel && monoId && !channel.isMonoforum) {
    actions.loadFullChat({ chatId: channel.id });
    if (await whenChatLoaded(monoId)) {
      actions.openChat({ id: monoId });
      return;
    }
  }
  // Monoforum couldn't be resolved/loaded — open the channel itself so the
  // operator at least lands on it (its native DM affordance still works),
  // rather than silently failing.
  if (channel) {
    actions.openChat({ id: channel.id });
    return;
  }
  postOpenChatFailed(origin);
}

async function openChatForCrm(target: CrmOpenChatTarget, origin: string) {
  const isReady = await whenClientReady();
  if (!isReady) {
    postOpenChatFailed(origin);
    return;
  }

  const actions = getActions();

  // Channel Direct Messages — open the linked monoforum via a load-then-open
  // sequence (see openChannelDirect).
  if (target.direct && target.username) {
    await openChannelDirect(target.username, origin);
    return;
  }

  // Username is the robust path: openChatByUsername resolves the peer from
  // scratch via the API, so it works even for a peer this account has never
  // talked to. Prefer it whenever the lead has a username.
  if (target.username) {
    actions.openChatByUsername({ username: target.username });
    return;
  }

  // By numeric id we can only open peers the account already knows —
  // openChat needs a cached access_hash (see global/actions/api/chats.ts).
  // The lead's resolved account is usually one that already corresponded,
  // so this hits; when it doesn't, tell the SPA so it can prompt a manual
  // search instead of opening nothing.
  if (target.peerId && selectUser(getGlobal(), target.peerId)) {
    actions.openChat({ id: target.peerId });
    return;
  }

  postOpenChatFailed(origin);
}

// Start listening for `tg:openChat` from the trusted parent window. Same
// origin/source allow-list as the session handshake. Idempotent.
export function listenForCrmChatOpen() {
  if (isListening) return;
  const origin = getCrmOrigin();
  if (!origin || !window.parent || window.parent === window) return;
  isListening = true;

  window.addEventListener('message', (event: MessageEvent) => {
    if (event.origin !== origin) return;
    if (event.source !== window.parent) return;
    const data: any = event.data;
    if (!data || data.type !== 'tg:openChat') return;

    const payload = data.payload;
    if (!payload || typeof payload !== 'object') return;

    const target: CrmOpenChatTarget = {
      peerId: typeof payload.peerId === 'string' ? payload.peerId : undefined,
      username: typeof payload.username === 'string' ? payload.username : undefined,
      direct: payload.direct === true || payload.direct === '1',
    };
    if (!target.peerId && !target.username) return;

    void openChatForCrm(target, origin);
  });
}
